import {
    Button,
    Input,
    InputGroup,
    InputRightAddon,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import PartBayButton from '../PartBayButton';
import {
    numberEntryError,
    UNIT_FIELD_ADDON_RADII,
    UNIT_FIELD_INPUT_RADII,
} from './primitives';

export interface VendorsBulkDiscountModalProps {
    isOpen: boolean;
    selectedCount: number;
    isSaving: boolean;
    /** Whole percent, or null to clear the discount on every selected vendor. */
    onApply: (percent: number | null) => void;
    onClose: () => void;
}

/**
 * One discount, applied to every selected vendor. Clearing the field clears
 * the discount, matching the inline cell.
 */
const VendorsBulkDiscountModal = ({
    isOpen,
    selectedCount,
    isSaving,
    onApply,
    onClose,
}: VendorsBulkDiscountModalProps) => {
    const [draft, setDraft] = useState('');

    // Reset between openings so a previous value never carries over.
    useEffect(() => {
        if (isOpen) setDraft('');
    }, [isOpen]);

    const trimmed = draft.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    const error = numberEntryError(draft, { min: 0, max: 100, allowDecimals: true });
    const isValid = error === null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader fontSize="md" fontWeight="semibold">
                    Set discount
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={2}>
                    <VStack align="stretch" spacing={4}>
                        <Text fontSize="sm" color="gray.500">
                            Applies to {selectedCount} selected vendor
                            {selectedCount === 1 ? '' : 's'}. Leave empty to clear their
                            discounts.
                        </Text>
                        <InputGroup size="md" maxW="180px">
                            <Input
                                type="text"
                                inputMode="decimal"
                                autoComplete="off"
                                autoFocus
                                isInvalid={error !== null}
                                placeholder="0"
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && isValid) onApply(parsed);
                                }}
                                {...UNIT_FIELD_INPUT_RADII}
                            />
                            <InputRightAddon {...UNIT_FIELD_ADDON_RADII}>%</InputRightAddon>
                        </InputGroup>
                        {!isValid && (
                            <Text fontSize="xs" color="red.500">
                                Enter a percentage between 0 and 100.
                            </Text>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button
                        size="sm"
                        variant="outlineGray"
                        onClick={onClose}
                        isDisabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <PartBayButton
                        size="sm"
                        variant="solid"
                        colorScheme="partbay-blue"
                        isDisabled={!isValid}
                        isLoading={isSaving}
                        loadingText="Applying"
                        onClick={() => onApply(parsed)}
                        text="Apply"
                    />
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default VendorsBulkDiscountModal;
